/**
 * @module Fuel HOC
 * @description Central source providers, avoids having multiple nested providers.
 */
import { createContext, useEffect, useState } from "react";
import ErrorBoundaryComponent from "@/blocks/ErrorBoundary";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"; // ReCaptcha Provider used to generate the token we pass in the 'frontierSiteDetailPredictive' cookie
import requestMonarch from "@/js/helpers/monarch";
import { useRouter } from "next/router";
import {
  isCartHeavy,
  isPhoneHeavy,
  isResumeQuote,
  updateOLVersion,
  quoteCreatedAfterCutoffDate,
  isOutageForm,
  isReturnVisitorOfferCookieEnabled,
  isPrepopulateResumeAddress,
} from "./utils/feature-flagging";
import useCartState from "@/hooks/useCartState";
import { getCookie } from "@/js/utils/cookie";
import { DEFAULT_OPEN_HOURS } from "@/js/constants/landingPage";
import { DEFAULT_FUSE_NUMBER, MONARCH_RULESETS } from "@/js/constants/monarch";
import { DEFAULT_CHAT_CONFIG } from "@/js/constants/chat";
import { resolveFrdcChatbotPayload } from "@/js/helpers/resolveFrdcChatbotPayload";
import { normalizeMonarchChatConfig } from "@/js/helpers/normalizeMonarchChatConfig";
import { setReturnVisitorCookie } from "@/js/utils/return-visitor-cookie";

/**
 * @function FuelContext
 * @description
 */
export const FuelContext = createContext();

/**
 * @function withFuel
 * @description
 */
export const withFuel = ({ App }) => {
  const FuelProviderHOC = (props) => {
    const router = useRouter();
    const { clickThrough, setClickThrough } = useCartState();

    const [monarchProduct, setMonarchProduct] = useState("fiber-gig");
    const [monarchCartHeavy, setMonarchCartHeavy] = useState(false);
    const [monarchPhoneHeavy, setMonarchPhoneHeavy] = useState(false);
    const [monarchOutageForm, setMonarchOutageForm] = useState(false);
    const [monarchResumeQuote, setMonarchResumeQuote] = useState(false);
    const [monarchQuoteCutoffDate, setMonarchQuoteCutoffDate] = useState(false);
    const [monarchOLVersion, setMonarchOLVersion] = useState("v2");
    const [monarchOpenHours, setMonarchOpenHours] = useState([]);
    const [fuseNumber, setFuseNumber] = useState(null);
    const [gdtVersion] = useState("/buy?buyFlow=NGC&opt-in=true");
    const [formInputted, setFormInputted] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [pauseCarousel, setPauseCarousel] = useState(false);
    const [suggestionsOpen, setSuggestionsOpen] = useState(false);
    const [isTabModalOpen, setIsTabModalOpen] = useState(false);

    useEffect(() => {
      const handleRouteChange = () => setSuggestionsOpen(false);
      router.events.on("routeChangeStart", handleRouteChange);
      return () => router.events.off("routeChangeStart", handleRouteChange);
    }, [router]);

    const [logRocketEnabled, setLogRocketEnabled] = useState(false);
    const [chatConfig, setChatConfig] = useState(DEFAULT_CHAT_CONFIG);
    const [
      monarchReturnVisitorOfferCookie,
      setMonarchReturnVisitorOfferCookie,
    ] = useState(false);
    const [isVerizon, setIsVerizon] = useState(false);
    const [
      monarchPrepopulateResumeAddress,
      setMonarchPrepopulateResumeAddress,
    ] = useState(false);

    useEffect(() => {
      // Create initial return visitor cookie on first page load (no Monarch flag required)
      const shownReturnVisitorOffer = Boolean(monarchReturnVisitorOfferCookie);

      setReturnVisitorCookie({ shownReturnVisitorOffer });

      cohesion("ready", () => {
        const apiVersion = getCookie("apiVersion");
        const audienceName = getCookie("audienceName");

        requestMonarch(
          "ruleset",
          MONARCH_RULESETS.LANDING_PAGE,
          { olVersion: apiVersion },
          {},
          (response) => {
            if (!response) return;

            const {
              determineProduct,
              cartHeavy,
              phoneHeavy,
              determineResumeQuote,
              determineOLVersion,
              attendHours,
              cartQuoteCutoffDate,
              landingPageNGCFlow,
              loadLogRocket,
              outageForm,
              frdcChatbot,
              returnVisitorOfferEligibility,
              verizonView,
              prepopulateResumeAddress,
            } = response;

            if (typeof determineProduct === "string")
              setMonarchProduct(determineProduct);

            if (isCartHeavy(cartHeavy, router.query)) setMonarchCartHeavy(true);

            // outageForm also trigger phone heavy behavior
            if (isPhoneHeavy(phoneHeavy || outageForm, router.query))
              setMonarchPhoneHeavy(true);

            if (isOutageForm(outageForm)) setMonarchOutageForm(true);

            if (verizonView) setIsVerizon(true);

            if (isResumeQuote(determineResumeQuote))
              setMonarchResumeQuote(true);

            if (quoteCreatedAfterCutoffDate(cartQuoteCutoffDate))
              setMonarchQuoteCutoffDate(true);

            if (updateOLVersion(determineOLVersion))
              setMonarchOLVersion(determineOLVersion);

            if (
              isReturnVisitorOfferCookieEnabled(
                returnVisitorOfferEligibility,
                router.query,
              )
            )
              setMonarchReturnVisitorOfferCookie(true);

            if (landingPageNGCFlow === true)
              setClickThrough("/buy?buyFlow=NGC&opt-in=true");

            if (attendHours?.data && Array.isArray(attendHours?.data)) {
              setMonarchOpenHours(attendHours.data);
            } else {
              setMonarchOpenHours(DEFAULT_OPEN_HOURS);
            }
            if (Array.isArray(loadLogRocket)) {
              const logRocket = loadLogRocket.find(
                (log) => log === audienceName,
              );
              if (logRocket) {
                setLogRocketEnabled(true);
              }
            }

            if (
              isPrepopulateResumeAddress(prepopulateResumeAddress, router.query)
            ) {
              setMonarchPrepopulateResumeAddress(true);
            }

            if (frdcChatbot) {
              const resolved = resolveFrdcChatbotPayload(frdcChatbot);
              const normalized = resolved
                ? normalizeMonarchChatConfig(resolved)
                : null;
              setChatConfig(normalized || DEFAULT_CHAT_CONFIG);
            } else {
              setChatConfig(DEFAULT_CHAT_CONFIG);
            }
          },
        );
      });
    }, [
      router,
      router.query,
      setClickThrough,
      monarchReturnVisitorOfferCookie,
    ]);

    useEffect(() => {
      window?.cohesion("fuse:leasesReceived", (leases) => {
        setFuseNumber(leases?.[0]?.dnis);
      });
      window?.cohesion("fuse:error", () => setFuseNumber(DEFAULT_FUSE_NUMBER));
    }, []);

    return (
      <FuelContext.Provider
        value={{
          monarchProduct,
          cartHeavy: monarchCartHeavy,
          phoneHeavy: monarchPhoneHeavy,
          outageForm: monarchOutageForm,
          clickThrough,
          resumeQuote: monarchResumeQuote,
          quoteCreatedAfterCutoffDate: monarchQuoteCutoffDate,
          olVersion: monarchOLVersion,
          formInputted,
          setFormInputted,
          isModalOpen,
          setIsModalOpen,
          pauseCarousel,
          setPauseCarousel,
          suggestionsOpen,
          setSuggestionsOpen,
          isTabModalOpen,
          setIsTabModalOpen,
          openHours: monarchOpenHours,
          fuseNumber,
          gdtVersion,
          logRocketEnabled,
          chatConfig,
          returnVisitorOfferCookieEnabled: monarchReturnVisitorOfferCookie,
          verizonView: isVerizon,
          prepopulateResumeAddress: monarchPrepopulateResumeAddress,
        }}
      >
        <ErrorBoundaryComponent>
          <GoogleReCaptchaProvider
            useEnterprise
            reCaptchaKey={`${process.env.reCaptchaSiteKey}`}
            scriptProps={{ defer: true }}
          >
            <App {...props} />
          </GoogleReCaptchaProvider>
        </ErrorBoundaryComponent>
      </FuelContext.Provider>
    );
  };

  return FuelProviderHOC;
};

export default withFuel;
